/**
 * Placement (§10.3): merge area defaults, task and mind constraints into one frozen placement, then match it
 * against runners. `match` is pure and returns a score or typed `Unmet` reasons. There is no fallback.
 */

import { AUTO_PLACED_AGENTS, RUNNER_FEATURES, type SecretReachRow } from "@minga/protocol"
import { choiceKey, chosenFor, mayRun, mayUseProfile, profileAccessOf, runAskerOf } from "./multiplayer"
import { secretUnmet } from "./secrets"
import type {
  AgentId,
  AgentKind,
  Ids,
  ModelsByAgent,
  Placement,
  PlacementDefaults,
  ProfileId,
  Requires,
  Run,
  RunId,
  RunnerFeature,
  RunnerId,
  RunnerManifest,
  RunnerProfile,
  RunnerRecord,
  Unmet,
} from "./types"

export const emptyPlacement: Placement = { requires: {}, prefers: [], affinity: null, exclusive: [] }

const union = <T>(
  a: ReadonlyArray<T> | undefined,
  b: ReadonlyArray<T> | undefined,
): ReadonlyArray<T> | undefined =>
  a === undefined ? b : b === undefined ? a : [...a, ...b.filter((x) => !a.includes(x))]

/** "Any of" lists intersect; undefined means "anything". Returns "empty" when nothing is left. */
const intersect = <T>(
  a: ReadonlyArray<T> | undefined,
  b: ReadonlyArray<T> | undefined,
): ReadonlyArray<T> | undefined | "empty" => {
  if (a === undefined) return b
  if (b === undefined) return a
  const out = a.filter((x) => b.includes(x))
  return out.length === 0 ? "empty" : out
}

/** Merge two `requires` (narrower can't remove what the wider one set). */
export const mergeRequires = (
  wide: Requires,
  narrow: Requires,
): { readonly ok: Requires } | { readonly invalid: string } => {
  const out: Record<string, unknown> = {}
  for (const k of ["agent", "os", "trust"] as const) {
    const r = intersect<string>(wide[k], narrow[k])
    if (r === "empty") return { invalid: `requires.${k}: no value satisfies every scope` }
    if (r !== undefined) out[k] = r
  }
  const tools = union(wide.tools, narrow.tools)
  if (tools) out.tools = tools
  const repos = union(wide.repos, narrow.repos)
  if (repos) out.repos = repos
  // decision 0141: secrets a run needs add up like tools
  const secrets = union(wide.secrets, narrow.secrets)
  if (secrets) out.secrets = secrets
  if (wide.labels || narrow.labels) {
    const labels: Record<string, string> = { ...(wide.labels ?? {}) }
    for (const [k, v] of Object.entries(narrow.labels ?? {})) {
      if (labels[k] !== undefined && labels[k] !== v)
        return { invalid: `requires.labels.${k}: conflicting values` }
      labels[k] = v
    }
    out.labels = labels
  }
  for (const k of ["runner", "profile"] as const) {
    const a = wide[k]
    const b = narrow[k]
    if (a !== undefined && b !== undefined && a !== b)
      return { invalid: `requires.${k}: pinned to two different values` }
    const v = a ?? b
    if (v !== undefined) out[k] = v
  }
  // decision 0095: places to avoid add up
  const avoid = [...(wide.avoid ?? []), ...(narrow.avoid ?? [])]
  if (avoid.length > 0) out.avoid = avoid
  return { ok: out as Requires }
}

/**
 * effective = merge(area defaults, task, mind or system constraints). prefers concatenate, exclusive keys
 * combine, all-of lists combine, any-of lists intersect. An empty intersection is invalid: never wait forever.
 */
export const mergePlacement = (
  areaDefaults: PlacementDefaults | Placement | null,
  task: Placement | null,
  mind: Placement | null,
): { readonly ok: Placement } | { readonly invalid: string } => {
  let requires: Requires = {}
  const prefers: Placement["prefers"][number][] = []
  const exclusive: string[] = []
  let affinity: Placement["affinity"] = null
  for (const p of [areaDefaults, task, mind]) {
    if (!p) continue
    const r = mergeRequires(requires, p.requires)
    if ("invalid" in r) return r
    requires = r.ok
    prefers.push(...p.prefers)
    for (const k of p.exclusive) if (!exclusive.includes(k)) exclusive.push(k)
    if ("affinity" in p && p.affinity) affinity = (p as Placement).affinity
  }
  return { ok: { requires, prefers, affinity, exclusive: exclusive as Placement["exclusive"] } }
}

/** "xcode@26" is met by "xcode@26" or "xcode@26.1"; "xcode" by any "xcode" or "xcode@*". */
export const toolSatisfies = (have: string, need: string): boolean => {
  const [hn, hv] = have.split("@")
  const [nn, nv] = need.split("@")
  if (hn !== nn) return false
  if (nv === undefined) return true
  if (hv === undefined) return false
  return hv === nv || hv.startsWith(`${nv}.`)
}

export type RunnerPresenceValue = "online" | "busy" | "away" | "offline" | "needs_attention"

/** What the hub knows right now, besides the runner rows. Keys: `${runnerId}/${profileId}` for profiles;
 * `ws:` exclusive keys as-is and `runner:` keys as `${runnerId}/${key}`. */
export interface HubView {
  readonly now: number
  readonly runningByProfile: ReadonlyMap<string, number>
  readonly freeSlots: ReadonlyMap<RunnerId, number>
  readonly exclusiveHeld: ReadonlyMap<string, RunId>
  readonly presence: ReadonlyMap<RunnerId, RunnerPresenceValue>
  /** Full workspace observations, retained when rechecking just one offered runner/profile. */
  readonly accountQuotas?: ReadonlyMap<string, ReadonlyArray<RunnerProfile["quota"][number]>>
  /** The runner and profile of each agent's last native session (soft `sessionOf` affinity). */
  readonly lastSessionOf?: ReadonlyMap<
    AgentId,
    { readonly runnerId: RunnerId; readonly profileId: ProfileId }
  >
  /**
   * Decision 0141: the workspace's secrets (metadata only), for `requires.secrets`. Absent = unknown: the names are
   * not checked (the delivery still sends only what reaches the run).
   */
  readonly secrets?: ReadonlyArray<SecretReachRow>
  /**
   * Decision 0149: points a loaded runner loses (`loadPenalty`: busy 15, Overloaded 40). A soft preference for a less
   * loaded runner when another fits; never a reason a run waits. Absent = no runner reports load.
   */
  readonly loadPenalty?: ReadonlyMap<RunnerId, number>
  /**
   * Decision 0166 §3.6: what each person chose for their work in each project (`choiceKey(userId, projectId)` →
   * `subscriptionKey`s). A person without an entry for a project chose nothing: everything they may use.
   */
  readonly choices?: ReadonlyMap<string, ReadonlyArray<string>>
}

/** Decision 0166 §3.6: the asker's choice for the run's project, or null when they chose nothing. */
export const chosenOf = (
  run: Pick<RunForMatch, "projectId" | "ownerId" | "kind" | "turnAsker">,
  hub: Pick<HubView, "choices">,
): ReadonlyArray<string> | null =>
  run.projectId ? (hub.choices?.get(choiceKey(runAskerOf(run), run.projectId)) ?? null) : null

export type RunForMatch = Pick<Run, "placement" | "ownerId" | "kind"> & {
  readonly projectId?: Ids["ProjectId"] | null
  readonly model?: string | null
  readonly byAgent?: ModelsByAgent
  readonly routing?: Run["routing"]
  readonly permission?: "read_only" | "workspace_write" | "workspace_write_network" | "full_auto"
  /** K14: manifest features the run needs (`["access_request"]` when its job carries a request). */
  readonly needs?: ReadonlyArray<RunnerFeature>
  /** Wave E (decision 0063): the run's item's lend (a runner its owner lent to the item); absent = none. */
  readonly lentRunnerId?: RunnerId | null
  /** Decision 0128: a turn's asker when a person other than the keeper (absent = the keeper). */
  readonly turnAsker?: Ids["UserId"] | null
  /**
   * Decision 0165: the item's check that looks heavy (core `heavyCheckOf`), set only when nothing routes the run to a
   * runner or profile. Inside a runner the run then prefers the profile with the bigger sandbox (`HEAVY_VCPU_WEIGHT`).
   */
  readonly heavyCheck?: string | null
}

/**
 * Decision 0165: points per vCPU of a profile's sandbox (`RunnerProfile.container`, Platform profiles only) when a heavy
 * run picks a profile inside one runner, up to 4 vCPU. standard-1 (½ vCPU) gets 13, standard-3 (2 vCPU) 50: more than
 * quota headroom (≤ 20) and free slots can make up, far less than the asker's own subscription (`OWN_RUNNER_BONUS`),
 * a preferred route or a cross-model preference. It only picks among the profiles one runner could start now (a busy
 * big profile leaves the run to the small one) and does not change how runners rank against each other.
 */
export const HEAVY_VCPU_WEIGHT = 25
const HEAVY_VCPU_CAP = 4

/** Decision 0165: what a profile adds for a heavy run when one runner picks among its profiles. */
export const heavySizeBonus = (run: Pick<RunForMatch, "heavyCheck">, p: RunnerProfile): number =>
  run.heavyCheck && p.container
    ? Math.round(Math.min(HEAVY_VCPU_CAP, p.container.vcpu) * HEAVY_VCPU_WEIGHT)
    : 0

const modelOf = (run: RunForMatch, p: RunnerProfile): string | null =>
  run.model ?? run.byAgent?.[p.agent]?.model ?? p.modelOverride ?? p.defaultModel ?? null

const quotaApplies = (q: RunnerProfile["quota"][number], model: string | null): boolean =>
  (!q.model || q.model === model) &&
  (!q.modelTag || model?.toLowerCase().includes(q.modelTag.toLowerCase()) === true)

/**
 * Wave E: `match` options. `ignoreOwnerLock` = would this runner take the run if its owner lent it (lend candidates)?
 * It lifts the profile lock too (decision 0128): would a subscription there run it if its owner shared it?
 */
export interface MatchOptions {
  readonly ignoreOwnerLock?: boolean
}

const quotaAccount = (p: RunnerProfile): string | null =>
  p.quotaAccountKey ? `${p.agent}:${p.auth}:${p.quotaAccountKey}` : null

/** Reconcile the latest report for each native limit across devices using the same known account. */
export const accountQuotasOf = (
  runners: ReadonlyArray<RunnerForMatch>,
): NonNullable<HubView["accountQuotas"]> => {
  const accounts = new Map<
    string,
    Map<string, { readonly at: number; readonly quota: RunnerProfile["quota"][number] }>
  >()
  for (const runner of runners)
    for (const p of runner.manifest.profiles) {
      const key = quotaAccount(p)
      if (key === null || p.quotaObservedAt === undefined) continue
      const limits =
        accounts.get(key) ??
        new Map<string, { readonly at: number; readonly quota: RunnerProfile["quota"][number] }>()
      for (const q of p.quota) {
        const id = q.id ?? q.window
        const previous = limits.get(id)
        if (
          !previous ||
          p.quotaObservedAt > previous.at ||
          (p.quotaObservedAt === previous.at && q.usedPercent > previous.quota.usedPercent)
        )
          limits.set(id, { at: p.quotaObservedAt, quota: q })
      }
      accounts.set(key, limits)
    }
  return new Map([...accounts].map(([key, limits]) => [key, [...limits.values()].map((q) => q.quota)]))
}

/**
 * Wave E (spec §3.3): a runner of the run's principal scores this much above a team or a lent one. Decision 0128: per
 * profile, the asker's own subscription scores it above a team one (on a local runner the profile is its owner's).
 */
export const OWN_RUNNER_BONUS = 1000
/**
 * Decision 0147: a cross-model validator's preference for the other CLI family, above the asker's own runner and a
 * preferred route (`OWN_RUNNER_BONUS * 2`), so the other family wins wherever it can run; never a requirement.
 */
export const CROSS_MODEL_WEIGHT = OWN_RUNNER_BONUS * 3
export type RunnerForMatch = RunnerRecord & { readonly manifest: RunnerManifest }

export type MatchResult =
  | { readonly _tag: "eligible"; readonly score: number; readonly profileId: ProfileId }
  | { readonly _tag: "unmet"; readonly reasons: ReadonlyArray<Unmet> }

export const exclusiveKeyFor = (runnerId: RunnerId, key: string): string =>
  key.startsWith("runner:") ? `${runnerId}/${key}` : key

/**
 * Decision 0118: the CLI families a run may land on. `requires.agent` when set; otherwise the automatic ones
 * (`AUTO_PLACED_AGENTS`: Claude Code, Codex) plus any CLI a `prefers` entry names. Antigravity and Muse Code are opt-in:
 * unrouted work never lands on them (before, an unpinned run could pick any family the runner offered, 0109/0110).
 */
export const eligibleAgents = (
  placement: Pick<Placement, "requires" | "prefers">,
): ReadonlyArray<AgentKind> => {
  if (placement.requires.agent) return placement.requires.agent
  const out = [...AUTO_PLACED_AGENTS]
  for (const pref of placement.prefers)
    for (const a of pref.match.agent ?? []) if (!out.includes(a)) out.push(a)
  return out
}

/** Does a runner's manifest satisfy a `requires` (used for hard requirements and for `prefers` matches)? */
const requirementsUnmet = (rq: Requires, runner: RunnerForMatch): Unmet | null => {
  const m = runner.manifest
  if (rq.runner !== undefined && rq.runner !== runner.id)
    return { _tag: "runner_offline", runnerId: rq.runner, lastSeenAt: null }
  if (rq.os && !rq.os.includes(m.platform.os))
    return {
      _tag: "ceiling_refuses",
      runnerId: runner.id,
      why: `os ${m.platform.os} not in ${rq.os.join("|")}`,
    }
  if (rq.trust && !rq.trust.includes(m.trust))
    return {
      _tag: "ceiling_refuses",
      runnerId: runner.id,
      why: `trust ${m.trust} not in ${rq.trust.join("|")}`,
    }
  for (const tool of rq.tools ?? [])
    if (!m.tools.some((h) => toolSatisfies(h, tool))) return { _tag: "missing_tool", tool }
  for (const repoId of rq.repos ?? [])
    if (!m.repos.some((r) => r.repoId === repoId)) return { _tag: "missing_repo", repoId }
  for (const [k, v] of Object.entries(rq.labels ?? {}))
    if (m.labels[k] !== v)
      return { _tag: "ceiling_refuses", runnerId: runner.id, why: `label ${k}=${v} missing` }
  return null
}

const profileUnmet = (
  run: RunForMatch,
  runner: RunnerForMatch,
  p: RunnerProfile,
  hub: HubView,
): Unmet | null => {
  const rq = run.placement.requires
  const key = quotaAccount(p)
  const shared = key === null ? undefined : hub.accountQuotas?.get(key)
  const quota = shared ?? p.quota
  if (rq.profile !== undefined && rq.profile !== p.id)
    return { _tag: "profile_out_of_scope", runnerId: runner.id, profileId: p.id }
  // decision 0095: an agent chose to avoid this runner or this profile on it
  if (rq.avoid?.some((a) => a.runner === runner.id && (a.profile === undefined || a.profile === p.id)))
    return { _tag: "profile_out_of_scope", runnerId: runner.id, profileId: p.id }
  if (
    p.readiness._tag === "quota_exhausted" &&
    shared === undefined &&
    (p.readiness.resetsAt === null || p.readiness.resetsAt > hub.now) &&
    !p.quota.some((q) => q.usedPercent >= 100 && (q.model || q.modelTag))
  )
    return { _tag: "rate_limited", runnerId: runner.id, profileId: p.id, resetsAt: p.readiness.resetsAt }
  if (p.readiness._tag !== "ready" && p.readiness._tag !== "quota_exhausted")
    return { _tag: "profile_not_ready", runnerId: runner.id, profileId: p.id, why: p.readiness._tag }
  const model = modelOf(run, p)
  const exhausted = quota.find(
    (q) => quotaApplies(q, model) && q.usedPercent >= 100 && (q.resetsAt === null || q.resetsAt > hub.now),
  )
  if (exhausted)
    return { _tag: "rate_limited", runnerId: runner.id, profileId: p.id, resetsAt: exhausted.resetsAt }
  if (run.projectId && p.projects !== null && !p.projects.includes(run.projectId))
    return { _tag: "profile_out_of_scope", runnerId: runner.id, profileId: p.id }
  if ((hub.runningByProfile.get(`${runner.id}/${p.id}`) ?? 0) >= p.maxConcurrent)
    return { _tag: "profile_at_capacity", runnerId: runner.id, profileId: p.id }
  return null
}

const quotaHeadroom = (quota: RunnerProfile["quota"], now: number): number => {
  const active = quota.filter(
    (q) => Number.isFinite(q.usedPercent) && (q.resetsAt === null || q.resetsAt > now),
  )
  // Unknown and expired observations are not evidence of a fully available account.
  if (active.length === 0) return 0
  const used = Math.max(...active.map((q) => q.usedPercent))
  return Math.max(0, Math.min(20, Math.round((100 - used) / 5)))
}

const COMPUTER_USE = "computer-use"

/**
 * match(run, runner, hub): hard filters in order (the first failure is the runner's reason), then a score.
 * 1 admin/presence · 2 owner lock (`mayRun`) · 3 ceiling · 4 requirements · 5 profile · 6 slots · 7 exclusive keys ·
 * 8 hard affinity. Wave E: the principal's own runners score `OWN_RUNNER_BONUS` above a team or a lent runner, so the
 * order is the asker's machines, then The team, then (only when lent) another person's machine.
 */
export const match = (
  run: RunForMatch,
  runner: RunnerForMatch,
  hub: HubView,
  opts: MatchOptions = {},
): MatchResult => {
  const unmet = (u: Unmet): MatchResult => ({ _tag: "unmet", reasons: [u] })
  const rq = run.placement.requires
  const m = runner.manifest
  // 1. admin state and presence
  if (runner.admin !== "active") return unmet({ _tag: "runner_paused", runnerId: runner.id })
  const presence = hub.presence.get(runner.id) ?? "offline"
  if (presence !== "online" && presence !== "busy")
    return unmet({ _tag: "runner_offline", runnerId: runner.id, lastSeenAt: runner.lastHeartbeatAt })
  // 2. owner lock (wave E, decision 0063): its owner's runs, any run when it serves the team, and the items lent to it
  if (!opts.ignoreOwnerLock && !mayRun(runner, run))
    return unmet({
      _tag: "owner_lock",
      principal: runAskerOf(run),
      runnerId: runner.id,
      ownerId: runner.ownerId,
    })
  // 3. ceiling
  const c = m.ceiling
  if (c.maxPermission === "read_only" && run.permission !== undefined && run.permission !== "read_only")
    return unmet({
      _tag: "ceiling_refuses",
      runnerId: runner.id,
      why: `permission ${run.permission} is above read_only`,
    })
  if (!c.allowRunKinds.includes(run.kind))
    return unmet({ _tag: "ceiling_refuses", runnerId: runner.id, why: `run kind ${run.kind}` })
  const wantAgents = eligibleAgents(run.placement)
  if (!wantAgents.some((a) => c.allowedAgents.includes(a)))
    return unmet({ _tag: "ceiling_refuses", runnerId: runner.id, why: `agents ${wantAgents.join("|")}` })
  for (const repoId of rq.repos ?? [])
    if (!c.allowedRepos.includes(repoId))
      return unmet({ _tag: "ceiling_refuses", runnerId: runner.id, why: `repo ${repoId}` })
  if ((rq.tools ?? []).some((t) => toolSatisfies(t, COMPUTER_USE)) && !c.allowComputerUse)
    return unmet({ _tag: "ceiling_refuses", runnerId: runner.id, why: "computer use" })
  // K14: features the run needs (a runner without them would ignore the request and run wider than asked)
  for (const f of run.needs ?? [])
    if (!(m.features ?? []).includes(f))
      return unmet({
        _tag: "runner_outdated",
        runnerId: runner.id,
        version: m.versions.minga,
        minimum: RUNNER_FEATURES[f],
      })
  // decision 0141: named secrets must reach the run (its project and asker) and the runner must deliver them
  const wantSecrets = rq.secrets ?? []
  if (wantSecrets.length > 0) {
    if (hub.secrets)
      for (const name of wantSecrets) {
        const u = secretUnmet(hub.secrets, name, {
          projectId: run.projectId ?? null,
          askerId: runAskerOf(run),
        })
        if (u)
          return unmet({
            _tag: "missing_secret",
            name,
            why: u.why,
            ...(u.ownerId ? { ownerId: u.ownerId as never } : {}),
          })
      }
    if (!(m.features ?? []).includes("secrets"))
      return unmet({
        _tag: "runner_outdated",
        runnerId: runner.id,
        version: m.versions.minga,
        minimum: RUNNER_FEATURES.secrets,
      })
  }
  // 4. requirements
  const req = requirementsUnmet(rq, runner)
  if (req) return unmet(req)
  // 5. a suitable profile (a profile pin names its CLI explicitly, decision 0118)
  const kinds = m.profiles.filter(
    (p) => (wantAgents.includes(p.agent) || rq.profile === p.id) && c.allowedAgents.includes(p.agent),
  )
  if (kinds.length === 0) return unmet({ _tag: "missing_agent", agent: [...wantAgents] })
  const reasons: Unmet[] = []
  const candidates: RunnerProfile[] = []
  // decision 0166 §3.6: the asker's choice for this project (null: nothing chosen)
  const chosen = chosenOf(run, hub)
  for (const p of kinds) {
    if (chosen !== null && !chosenFor(runner, p.id, { chosen })) {
      reasons.push({ _tag: "not_chosen", runnerId: runner.id, profileId: p.id, agent: p.agent })
      continue
    }
    // decision 0128 (0126 §5): the asker's own subscription or a team one; someone else's personal one never
    if (
      !opts.ignoreOwnerLock &&
      (rq.profile === undefined || rq.profile === p.id) &&
      !mayUseProfile(runner, p.id, { ...run, chosen })
    ) {
      reasons.push({
        _tag: "owner_lock",
        principal: runAskerOf(run),
        runnerId: runner.id,
        ownerId: profileAccessOf(runner, p.id).ownerId,
        profileId: p.id,
      })
      continue
    }
    const u = profileUnmet(run, runner, p, hub)
    if (u) reasons.push(u)
    else candidates.push(p)
  }
  if (candidates.length === 0) return { _tag: "unmet", reasons }
  // 6. free slots
  const free = hub.freeSlots.get(runner.id) ?? 0
  if (free <= 0) return unmet({ _tag: "at_capacity", runnerId: runner.id })
  // 7. exclusive keys
  for (const key of run.placement.exclusive) {
    const heldBy = hub.exclusiveHeld.get(exclusiveKeyFor(runner.id, key))
    if (heldBy !== undefined) return unmet({ _tag: "exclusive_held", key, heldBy })
  }
  // 8. hard affinity
  const aff = run.placement.affinity
  const hasWorktree = (taskId: string) => m.repos.some((r) => r.worktrees.some((w) => w.taskId === taskId))
  if (aff?.strength === "hard" && aff.worktreeOf !== undefined && !hasWorktree(aff.worktreeOf))
    return unmet({ _tag: "affinity_unmet", worktreeOf: aff.worktreeOf })
  // score: prefers, soft affinity, quota headroom, free slots. Inside the runner the profile is chosen the same way.
  let base = 0
  if (aff?.worktreeOf !== undefined && hasWorktree(aff.worktreeOf)) base += 50
  base += Math.min(10, free)
  // decision 0149: a loaded machine scores lower, so a less loaded one that fits wins; it still takes the run alone
  base -= hub.loadPenalty?.get(runner.id) ?? 0
  const last = aff?.sessionOf !== undefined ? hub.lastSessionOf?.get(aff.sessionOf) : undefined
  // decision 0165: `rank` adds the heavy run's size preference, which only chooses among this runner's profiles;
  // the runner's score (what `pickRunner` compares across runners) leaves it out
  let best: { p: RunnerProfile; rank: number } | null = null
  let top = Number.NEGATIVE_INFINITY
  for (const p of candidates) {
    const account = quotaAccount(p)
    const quota = account === null ? p.quota : (hub.accountQuotas?.get(account) ?? p.quota)
    // decision 0128: the asker's own subscription first (wave E: the principal's own runner)
    const own = profileAccessOf(runner, p.id).ownerId === runAskerOf(run) ? OWN_RUNNER_BONUS : 0
    let score =
      base +
      own +
      quotaHeadroom(
        quota.filter((q) => quotaApplies(q, modelOf(run, p))),
        hub.now,
      )
    for (const pref of run.placement.prefers)
      if (
        requirementsUnmet(pref.match, runner) === null &&
        (!pref.match.agent || pref.match.agent.includes(p.agent)) &&
        (pref.match.profile === undefined || pref.match.profile === p.id)
      )
        score += pref.weight
    if (last && last.runnerId === runner.id && last.profileId === p.id) score += 30
    const rank = score + heavySizeBonus(run, p)
    if (score > top) top = score
    if (!best || rank > best.rank || (rank === best.rank && p.id < best.p.id)) best = { p, rank }
  }
  if (!best) return { _tag: "unmet", reasons }
  // the runner scores as its best profile would without the size preference (the same as before 0165)
  return { _tag: "eligible", score: top, profileId: best.p.id }
}

/** Order Unmet reasons by how actionable they are (what the canvas shows first). */
const ACTIONABILITY: ReadonlyArray<Unmet["_tag"]> = [
  "missing_secret",
  "missing_tool",
  "missing_repo",
  "missing_agent",
  "affinity_unmet",
  "ceiling_refuses",
  "runner_outdated",
  "profile_not_ready",
  "profile_out_of_scope",
  "rate_limited",
  "runner_offline",
  "runner_paused",
  "owner_lock",
  "not_chosen",
  "exclusive_held",
  "profile_at_capacity",
  "at_capacity",
  "no_runner_online",
]

/** Best eligible runner (ties → lowest runnerId), or the reasons the run must wait. */
export const pickRunner = (
  run: RunForMatch,
  runners: ReadonlyArray<RunnerForMatch>,
  hub: HubView,
  exclude: ReadonlySet<RunnerId> = new Set(),
):
  | {
      readonly _tag: "eligible"
      readonly runnerId: RunnerId
      readonly profileId: ProfileId
      readonly score: number
    }
  | { readonly _tag: "unmet"; readonly reasons: ReadonlyArray<Unmet> } => {
  let best: { runnerId: RunnerId; profileId: ProfileId; score: number } | null = null
  const reasons: Unmet[] = []
  const seen = new Set<string>()
  const view = hub.accountQuotas ? hub : { ...hub, accountQuotas: accountQuotasOf(runners) }
  let anyOnline = false
  // Incident 2026-10-15: a run pinned to one runner (a runner mind's `requires.runner`) is judged on that runner
  // alone. Other runners never take it, so they add no reasons: before, each of them reported the pin as
  // "runner_offline, never seen" (the pin was online) and their own presence ("ubuntu is offline") as noise.
  const pin = run.placement.requires.runner
  for (const r of [...runners].sort((a, b) => (a.id < b.id ? -1 : 1))) {
    if (pin !== undefined && r.id !== pin) continue
    const pres = hub.presence.get(r.id)
    if (pres === "online" || pres === "busy") anyOnline = true
    if (exclude.has(r.id)) continue
    const res = match(run, r, view)
    if (res._tag === "eligible") {
      if (!best || res.score > best.score)
        best = { runnerId: r.id, profileId: res.profileId, score: res.score }
    } else {
      for (const u of res.reasons) {
        const k = JSON.stringify(u)
        if (!seen.has(k)) {
          seen.add(k)
          reasons.push(u)
        }
      }
    }
  }
  if (best) return { _tag: "eligible", ...best }
  // a pinned runner that isn't registered at all still waits for it
  if (pin && !runners.some((r) => r.id === pin))
    reasons.push({ _tag: "runner_offline", runnerId: pin, lastSeenAt: null })
  // a pinned run waits for its pin only: the pin's own reason says it ("Platform Runner is offline since 04:13")
  if (!anyOnline && pin === undefined) reasons.push({ _tag: "no_runner_online" })
  reasons.sort((a, b) => ACTIONABILITY.indexOf(a._tag) - ACTIONABILITY.indexOf(b._tag))
  return { _tag: "unmet", reasons }
}

/**
 * Reasons that mean a runner and profile would take the run but are busy right now (online, not limited): the run
 * starts as soon as a slot or key frees up.
 */
const BUSY_ONLY: ReadonlySet<Unmet["_tag"]> = new Set<Unmet["_tag"]>([
  "profile_at_capacity",
  "at_capacity",
  "exclusive_held",
  "affinity_unmet",
])

/**
 * Incident 2026-10-15: is the run waiting only because every eligible profile is rate limited? True when a reason is
 * `rate_limited` and none says an online, unlimited runner is merely busy (offline, paused or unsuited runners don't
 * count: they would not start it either). `resetsAt` is the earliest known reset (null: none known). A chat turn in
 * this state ends `rate_limited` at once instead of waiting hours; worker and validator runs keep waiting and start
 * after the reset.
 */
export const rateLimitedOnly = (
  unmet: ReadonlyArray<Unmet>,
): {
  readonly resetsAt: number | null
  readonly limited: ReadonlyArray<Extract<Unmet, { _tag: "rate_limited" }>>
} | null => {
  const limited = unmet.filter(
    (u): u is Extract<Unmet, { _tag: "rate_limited" }> => u._tag === "rate_limited",
  )
  if (limited.length === 0 || unmet.some((u) => BUSY_ONLY.has(u._tag))) return null
  const known = limited.flatMap((u) => (u.resetsAt !== null ? [u.resetsAt] : []))
  return { resetsAt: known.length > 0 ? Math.min(...known) : null, limited }
}
